// import Keycloak from 'keycloak-js';
// Keycloak is imported in public/index.html via <script>, because webpack can't handle it
// If you want to edit something here, you can uncomment the import above to get intellisense
// Don't forget to comment it back before commiting or trying to run, because it won't work
// @ts-nocheck
import { UnauthorizedError } from '../types/CustomErrors';

const keycloakConfig = require('./keycloak.json') as Record<string, string>;

class SecurityService {
    // eslint-disable-next-line no-undef
    inst: Keycloak;
    private logoutInProgress = false;

    get token() {
        return this.inst.token!;
    }

    constructor() {
        if (window.RUNTIME_CONFIG?.keycloakConfig) {
            const config = {
                ...keycloakConfig,
                ...window.RUNTIME_CONFIG.keycloakConfig
            };

            console.log(config);

            // eslint-disable-next-line no-undef
            this.inst = new Keycloak(config);
        } else {
            // Rise error
            console.error('No runtime config found');
            throw new Error('No runtime config found');
        }
    }

    init() {
        return new Promise((res, rej) => {
            const promise = this.inst.init({ onLoad: 'login-required', checkLoginIframe: false, pkceMethod: 'S256' });
            promise
                .then(auth => {
                    if (auth) {
                        res({ authenticated: true });
                    } else {
                        rej(new UnauthorizedError());
                    }
                })
                .catch(err => {
                    console.warn(err);
                    rej(err);
                });
        });
    }

    getOrRefreshToken(): Promise<{ token: string; refreshed: boolean }> {
        if (!this.inst.isTokenExpired) {
            return Promise.resolve({ token: this.inst.token!, refreshed: false });
        }

        return new Promise<{ token: string; refreshed: boolean }>((res, rej) => {
            this.inst
                .updateToken(30)
                .then(refreshed => {
                    if (refreshed) {
                        console.log('Token was updated');
                    }

                    res({ token: this.inst.token!, refreshed: true });
                })
                .catch(err => {
                    console.error('Error during token refresh');
                    rej(err);
                });
        });
    }

    invoke<T>(func: (token: string) => Promise<T>): Promise<T> {
        return new Promise<T>((res, rej) => {
            if (!this.inst.isTokenExpired(30)) {
                func(this.inst.token!)
                    .then(s => res(s))
                    .catch(err => rej(err));
                return;
            }

            console.log('Token is expired');
            this.inst
                .updateToken(30)
                .then(refreshed => {
                    if (refreshed) {
                        console.log('Token was updated');
                    }

                    func(this.inst.token!)
                        .then(s => res(s))
                        .catch(err => rej(err));
                })
                .catch(err => {
                    console.error('Error during token refresh');
                    rej(err);
                });
        });
    }

    logoutCurrentSession() {
        if (this.logoutInProgress) {
            return;
        }

        this.logoutInProgress = true;
        this.inst.clearToken?.();
        this.inst.logout();
    }
}

class MockedSecurityService {
    // eslint-disable-next-line no-undef
    inst: Keycloak;
    cachedUserInfo: Promise<{}>;

    constructor() {
        // Don't call super() to avoid runtime config check in tests
        // eslint-disable-next-line no-undef
        this.inst = {} as Keycloak;
        this.cachedUserInfo = Promise.resolve({});
    }

    get token() {
        return 'empty';
    }

    init() {
        return Promise.resolve(true);
    }

    getOrRefreshToken(): Promise<{ token: string; refreshed: boolean }> {
        return Promise.resolve({ token: this.token, refreshed: false });
    }

    invoke<T>(func: (token: string) => Promise<T>): Promise<T> {
        return new Promise<T>((res, rej) => {
            func(this.token)
                .then(s => res(s))
                .catch(err => rej(err));
        });
    }

    logoutCurrentSession() {
        return;
    }

    loadUserInfo() {
        return this.cachedUserInfo;
    }
}

let service: SecurityService;
if (process.env.NODE_ENV !== 'test') {
    service = new SecurityService();
} else {
    // console.debug('Authentication is not enabled');
    service = new MockedSecurityService();
}

export default service;
